import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import moment from 'moment';
import CircularProgress from '@material-ui/core/CircularProgress';

import { translate } from 'services/i18nService';
import ThemeService from 'themes/ThemeService';
import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import GamesStore from 'stores/GamesStore';
import PaymentStore from 'stores/PaymentStore';
import BalanceInfoMobile from 'components/BalanceInfoMobile/BalanceInfoMobile';
import Button from 'components/Button/Button';
import ErrorAlert from 'components/ErrorAlert/ErrorAlert';
import { SECOND_AS_MILLISECONDS } from 'enum/timeFormat';
import { GAMES, GAMES_STATUS } from 'utils/consts';

import styles from './TokenKeno.css';


const CLOSE_DELAY = 2 * SECOND_AS_MILLISECONDS;

const TokenKeno = (props) => {
  const {
    embedProfileV2,
    isDesktop,
    roomName,
    setShowRegistrationModal,
    showBuyTokensModal,
    toggleGameDrawer,
    user,
  } = props;

  const {
    clearResult,
    isPlayActionLoading,
    lowestPrice,
    pauseTime,
    prizes,
    prizeList,
    result,
    status,
    tokenKenoClick,
  } = GamesStore;
  const { apiToken } = FirebaseStore;
  const { balance } = PaymentStore;

  const [timer, setTimer] = useState(null);
  const [isPlayed, setIsPlayed] = useState(false);
  const [noFunds, setnoFunds] = useState(false);
  const [clickedSquare, setClickedSquare] = useState(null);

  const minFunds = balance >= lowestPrice;

  useEffect(() => {
    if (apiToken) {
      const data = {
        game: GAMES.TOKEN_SQUARES,
        roomName,
        token: apiToken,
      };
      GamesStore.getGameInfo(data);
    }
  }, [roomName, apiToken]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (pauseTime && status === GAMES_STATUS.PAUSE) {
      const countdown = setInterval(() => {
        const timeLeft = moment(pauseTime).diff(moment());
        const formatted = moment(timeLeft).format('mm:ss');
        if (timeLeft < 0) {
          setTimer(null);
          setIsPlayed(false);
          clearInterval(countdown);
        } else {
          setTimer(formatted);
        }
      }, SECOND_AS_MILLISECONDS);

      return () => {
        clearInterval(countdown);
      };
    }
    setTimer(null);
  }, [pauseTime, status]);

  const handleDrawer = useCallback(() => {
    if (!isDesktop) {
      toggleGameDrawer();
    } else {
      clearResult();
      ChatStore.setProfileChatTabValue(0);
    }
  }, [isDesktop, toggleGameDrawer, clearResult]);

  useEffect(() => {
    let closeTimeout = null;
    if (result && isPlayed) {
      closeTimeout = setTimeout(() => {
        if (!isDesktop) {
          setIsPlayed(false);
        }
        handleDrawer();
      }, CLOSE_DELAY);
    }

    return () => {
      clearTimeout(closeTimeout);
    };
  }, [result, handleDrawer, isPlayed, isDesktop]);

  const onShowBuyTokensModal = useCallback(() => {
    if (embedProfileV2 && !user.username) {
      setShowRegistrationModal(true);
    } else {
      showBuyTokensModal();
    }
  }, [embedProfileV2, showBuyTokensModal, setShowRegistrationModal, user.username]);

  const onOpenBuyTokensModal = useCallback(() => {
    handleDrawer();
    onShowBuyTokensModal();
  }, [handleDrawer, onShowBuyTokensModal]);

  const onClick = useCallback((index, price, disabled = false) => () => {
    if (disabled || (isPlayed && timer)) {
      return;
    }

    if (parseInt(balance, 10) < parseInt(price, 10) || !user.username) {
      setnoFunds(true);
      onOpenBuyTokensModal();
    } else {
      setIsPlayed(true);
      setClickedSquare(index);
      tokenKenoClick(roomName, apiToken, index);
    }
  }, [
    apiToken,
    balance,
    isPlayed,
    roomName,
    timer,
    tokenKenoClick,
    user.username,
    onOpenBuyTokensModal,
  ]);

  const kenoTheme = ThemeService.theme.games.keno;

  const containerClasses = classNames(
    styles.container,
    kenoTheme.containerClass,
    { [styles.containerDesktop]: isDesktop },
  );

  const borderClasses = classNames(styles.border, kenoTheme.borderClass);

  const balanceInfoElement = useMemo(() => {
    if (!isDesktop) {
      return (
        <BalanceInfoMobile
          areTokensNeeded={balance === 0}
          id="tokenKeno_buyTokens_mobile"
          onClick={onOpenBuyTokensModal}
        />
      );
    }

    if (noFunds && !minFunds) {
      return (
        <div className={styles.noFunds}>
          <ErrorAlert message={translate('v2profile.insufficient.balance')} />
          <Button
            colour="green"
            id="tokenKeno_buyTokens_desktop"
            label={translate('v2profile.buy.tokens')}
            onClick={onOpenBuyTokensModal}
          />
        </div>
      );
    }

    return null;
  }, [
    balance,
    isDesktop,
    minFunds,
    noFunds,
    onOpenBuyTokensModal,
  ]);

  return (
    <div className={containerClasses}>
      <div className={styles.title}>
        {timer ? <span className={styles.timer}>{timer}</span> : translate('v2profile.click.token.square')}
      </div>
      <div className={borderClasses} />
      <ul className={styles.prizeList}>
        {prizeList.map((el, index) => (
          <li
            key={`${el.pr}${index}`}
            className={`${el.dis ? styles.pDisabled : ''} ${result === el.pr && isPlayed ? styles.blink : ''}`}
          >
            {el.pr}
          </li>
        ))}
      </ul>
      <div className={borderClasses} />
      <div className={styles.priceList}>
        {prizes &&
          prizes.map((el, index) => (
            <span
              key={`${el.pr}${index}_pr`}
              className={`${styles.price} ${el.dis ? styles.priceDisabled : ''}`}
              id={`tokenKeno_square_${index + 1}_${isDesktop ? 'desktop' : 'mobile'}`}
              onClick={onClick(index, el.p, el.dis)}
            >
              {isPlayActionLoading && index === clickedSquare ? <CircularProgress color="inherit" size={16} /> : el.p}
            </span>
          ))}
      </div>
      {balanceInfoElement}
    </div>
  );
};

TokenKeno.propTypes = {
  roomName: PropTypes.string.isRequired,
  showBuyTokensModal: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  embedProfileV2: PropTypes.bool,
  isDesktop: PropTypes.bool,
  setShowRegistrationModal: PropTypes.func,
  toggleGameDrawer: PropTypes.func,
};

TokenKeno.defaultProps = {
  embedProfileV2: false,
  isDesktop: false,
  setShowRegistrationModal: () => undefined,
  toggleGameDrawer: () => undefined,
};

export default observer(TokenKeno);
